import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "partials/Footer";
import Breadcrumb, { BreadcrumbItem } from "components/Breadcrumb";
import Button from "components/Button";
import Input from "components/form/Input";
import Textarea from "components/form/Textarea";
import RichTextEditor from "components/form/RichTextEditor"

const BASE_URL = "https://api.kennapartners.com/api/v1";

const Article = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [tempArrayInput, setTempArrayInput] = useState({
    authors: "",
  });
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState("");
  const navigate = useNavigate();

  const isInitialLoadRef = useRef(true); // Track if this is initial load
  const isSearchingRef = useRef(false); // Track if we're currently searching
  const allArticlesCacheRef = useRef(null); // Cache all articles to avoid repeated API calls

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    practice_area: "",
    authors: [],
    image: null,
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchArticles();
    fetchPracticeAreas();
    fetchAuthors();
  }, [currentPage]); // Re-fetch articles when page changes

  // Initialize filteredArticles ONLY on initial load
  useEffect(() => {
    if (articles.length > 0 && isInitialLoadRef.current && !isSearchingRef.current) {
      setFilteredArticles(articles);
      isInitialLoadRef.current = false; // Mark that initial load is done
    }
  }, [articles]);

  // Search functionality
  useEffect(() => {
    if (searchTerm.trim()) {
      isSearchingRef.current = true;
      const debounceTimer = setTimeout(() => {
        searchAllArticles(searchTerm.trim());
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      // Search cleared - reset to current page
      isSearchingRef.current = false;
      setFilteredArticles(articles);
    }
  }, [searchTerm]);

  const fetchArticles = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        navigate("/login");
        return;
      }
  
      const response = await fetch(`${BASE_URL}/articles/?page=${currentPage}&limit=${itemsPerPage}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("access_token");
          navigate("/login");
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      if (data && data.data && Array.isArray(data.data.articles)) {
        setArticles(data.data.articles);
        setFilteredArticles(data.data.articles); // Initialize filtered articles
        setTotalItems(data.data.total_practice_area || 0);
      } else {
        console.error(
          'Invalid data format. Expected an array of articles under "data.articles".'
        );
        setArticles([]);
        setFilteredArticles([]);
        setTotalItems(0);
      }
      setError("");
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError(error.message || "Failed to fetch articles");
      setArticles([]);
      setFilteredArticles([]);
      setTotalItems(0);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };
  

const fetchPracticeAreas = async () => {
  try {
    const token = localStorage.getItem("access_token");
    let allPracticeAreas = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(`${BASE_URL}/practice-areas/?page=${page}&limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const fetched = data.data?.practice_areas || [];

      allPracticeAreas = [...allPracticeAreas, ...fetched];
      hasMore = fetched.length === 50;
      page += 1;
    }

    allPracticeAreas.sort((a, b) => a.name.localeCompare(b.name));
    setPracticeAreas(allPracticeAreas);
  } catch (error) {
    console.error("Error fetching practice areas:", error);
  }
};


const fetchAuthors = async () => {
  try {
    const token = localStorage.getItem("access_token");
    let allAuthors = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await fetch(`${BASE_URL}/profiles/?page=${page}&limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const fetched = data.data?.profiles || [];

      allAuthors = [...allAuthors, ...fetched];
      hasMore = fetched.length === 50;
      page += 1;
    }

    allAuthors.sort((a, b) => a.full_name.localeCompare(b.full_name));
    setAuthors(allAuthors);
  } catch (error) {
    console.error("Error fetching authors:", error);
  }
};


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleArrayInputChange = (e) => {
    const { name, value } = e.target;
    setTempArrayInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addArrayItem = (field) => {
    if (field === "authors") {
      const selectedId = tempArrayInput[field];
      if (!selectedId) return;

      const selectedAuthor = authors.find((author) => author.id === selectedId);
      if (!selectedAuthor) return;

      const alreadyExists = formData.authors.some(
        (author) =>
          (typeof author === "object" ? author.id : author) === selectedId
      );
      if (alreadyExists) {
        setError("This author is already added");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: [...prev[field], selectedAuthor],
      }));
    }

    setTempArrayInput((prev) => ({
      ...prev,
      [field]: "",
    }));
    setError("");
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleFileUpload = async (articleId) => {
    if (!file) {
      console.log("No file to upload");
      return null;
    }
  
    try {
      const token = localStorage.getItem("access_token");
      const formDataToSend = new FormData();
      formDataToSend.append("file", file);
  
      const response = await fetch(`${BASE_URL}/articles/${articleId}/upload`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        console.error("File upload failed:", errorData);
        throw new Error(errorData.message || "Failed to upload file");
      }
  
      return await response.json();
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

const handleCreateArticle = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError("");

  try {
    const imageUrls = [];
    const tempContent = formData.content;
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    let match;

    while ((match = imgRegex.exec(tempContent)) !== null) {
      if (match[1].startsWith("blob:")) {
        imageUrls.push(match[1]);
      }
    }

    const uploadedImageUrls = await Promise.all(
      imageUrls.map(async (tempUrl, index) => {
        try {
          const response = await fetch(tempUrl);
          const blob = await response.blob();
          const file = new File([blob], `image-${Date.now()}.png`, { type: blob.type });

          const formDataToSend = new FormData();
          formDataToSend.append("file", file);

          const token = localStorage.getItem("access_token");
          const uploadResponse = await fetch(`${BASE_URL}/articles/editor/upload`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formDataToSend,
          });

          const responseData = await uploadResponse.json();
          const imageUrl = responseData?.data?.file_url;
          if (!imageUrl) {
            throw new Error("Server response missing image URL");
          }
          return { tempUrl, permUrl: imageUrl };
        } catch (error) {
          console.error(`Error uploading image ${index + 1} (${tempUrl}):`, error);
          return { tempUrl, permUrl: null, error: error.message };
        }
      })
    );

    let cleanedContent = tempContent;
    const failedUploads = [];

    uploadedImageUrls.forEach(({ tempUrl, permUrl, error }) => {
      if (permUrl) {
        cleanedContent = cleanedContent.replace(
          new RegExp(escapeRegExp(tempUrl), "g"),
          permUrl
        );
      } else {
        failedUploads.push({ tempUrl, error });
      }
    });

    cleanedContent = cleanedContent.replace(/<img[^>]*src="blob:[^"]*"[^>]*>/g, "");

    if (failedUploads.length > 0) {
      setError(`Warning: ${failedUploads.length} images could not be uploaded and were removed`);
    }

    const payload = {
      title: formData.title,
      content: {
        html: cleanedContent,
        text: cleanedContent.replace(/<[^>]*>/g, ""),
      },
      practice_area: formData.practice_area,
      date: new Date(formData.date).toISOString(),
      author: formData.authors.map((author) => author.id),
    };

    const token = localStorage.getItem("access_token");
    const response = await fetch(`${BASE_URL}/articles/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(
        responseData.detail?.[0]?.msg ||
        responseData.message ||
        `Server responded with status ${response.status}`
      );
    }

    if (file) {
      try {
        await handleFileUpload(responseData.data.article.id);
      } catch (fileError) {
        console.error("Main image upload failed:", fileError);
      }
    }

    setShowCreateModal(false);
    resetForm();
    setSuccessMessage("Article created successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
    await fetchArticles(false);
  } catch (error) {
    console.error("Article creation process failed:", error);
    setError(
      error.message ||
      "Please check your inputs and try again. Ensure all required fields are filled."
    );
  } finally {
    setLoading(false);
  }
};

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
 

const handleUpdateArticle = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  if (!selectedId) {
    setError("No article selected for update");
    setLoading(false);
    return;
  }

  const imageUrls = [];
  const tempContent = formData.content;
  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  let match;

  while ((match = imgRegex.exec(tempContent)) !== null) {
    if (match[1].startsWith("blob:")) {
      imageUrls.push(match[1]);
    }
  }

  const uploadedImageUrls = await Promise.all(
    imageUrls.map(async (tempUrl, index) => {
      try {
        const response = await fetch(tempUrl);
        const blob = await response.blob();
        const file = new File([blob], `image-${Date.now()}.png`, { type: blob.type });

        const formDataToSend = new FormData();
        formDataToSend.append("file", file);

        const token = localStorage.getItem("access_token");
        const uploadResponse = await fetch(`${BASE_URL}/articles/editor/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        });

        const responseData = await uploadResponse.json();
        const imageUrl = responseData?.data?.file_url;
        if (!imageUrl) {
          throw new Error("Server response missing image URL");
        }

        return { tempUrl, permUrl: imageUrl };
      } catch (error) {
        console.error(`Error uploading image ${index + 1} (${tempUrl}):`, error);
        return { tempUrl, permUrl: null, error: error.message };
      }
    })
  );

  let cleanedContent = tempContent;
  const failedUploads = [];

  uploadedImageUrls.forEach(({ tempUrl, permUrl, error }) => {
    if (permUrl) {
      cleanedContent = cleanedContent.replace(
        new RegExp(escapeRegExp(tempUrl), "g"),
        permUrl
      );
    } else {
      failedUploads.push({ tempUrl, error });
    }
  });

  cleanedContent = cleanedContent.replace(/<img[^>]*src="blob:[^"]*"[^>]*>/g, "");

  if (failedUploads.length > 0) {
    setError(`Warning: ${failedUploads.length} images could not be uploaded and were removed`);
  }

  const payload = {
    title: formData.title,
    content: {
      html: cleanedContent,
      text: cleanedContent.replace(/<[^>]*>/g, ""),
    },
    practice_area: formData.practice_area,
    date: new Date(formData.date).toISOString(),
    author: formData.authors.map((author) => author.id),
  };

  try {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${BASE_URL}/articles/${selectedId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    if (file) {
      try {
        await handleFileUpload(selectedId);
      } catch (uploadError) {
        console.error("File upload error:", uploadError);
      }
    }

    setShowEditModal(false);
    resetForm();
    setSuccessMessage("Article updated successfully!");
    setTimeout(() => setSuccessMessage(""), 3000);
    await fetchArticles();
  } catch (error) {
    console.error("Error updating article:", error);
    setError(error.message || "Failed to update article");
  } finally {
    setLoading(false);
  }
};

  const handleDeleteArticle = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL}/articles/${selectedId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `HTTP error! status: ${response.status}`
        );
      }
  
      setArticles((prev) =>
        prev.filter((article) => article.id !== selectedId)
      );
      setShowDeleteModal(false);
      setSuccessMessage("Article deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      if (filteredArticles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchArticles();
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error.message || "Failed to delete article. Please try again.");
    } finally {
      setLoading(false);
    }
  };

const openDetailsModal = async (articleId) => {
  setLoading(true);
  setShowDetailsModal(true);
  try {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${BASE_URL}/articles/${articleId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    setCurrentArticle({
      ...data.data.article,
      content: typeof data.data.article.content === 'string' 
        ? data.data.article.content 
        : data.data.article.content.html || ''
    });
    setError("");
  } catch (error) {
    console.error("Error fetching article details:", error);
    setError("Failed to load article details");
    setCurrentArticle(null);
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      practice_area: "",
      authors: [],
      image: null,
      date: new Date().toISOString().split('T')[0],
    });
    setTempArrayInput({
      authors: "",
    });
    setFile(null);
    setError("");
  };

  const resetAllFormStates = () => {
    setSelectedId(null);
    setCurrentArticle(null);
    resetForm();
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

const openEditModal = (article) => {
  setFormData({
    title: article.title || "",
    content: typeof article.content === 'string' ? article.content : article.content.html || "",
    practice_area: article.practice_area?.id || "",
    authors: article.author || [],
    image: null,
    date: article.date ? article.date.split('T')[0] : new Date().toISOString().split('T')[0],
  });
  setSelectedId(article.id);
  setShowEditModal(true);
};


  const openDeleteModal = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    resetAllFormStates();
  };

  const renderArrayItems = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return items.map((item, index) => (
      <span
        key={index}
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
      >
        {typeof item === "object" ? item.full_name : item}
        <button
          type="button"
          onClick={() => removeArrayItem("authors", index)}
          className="ml-1 text-blue-600 hover:text-blue-900"
        >
          ×
        </button>
      </span>
    ));
  };

  const searchAllArticles = async (term) => {
    setLoading(true);
    try {
      let allArticles = allArticlesCacheRef.current;

      // Only fetch if we don't have cached data
      if (!allArticles) {
        const token = localStorage.getItem("access_token");
        const response = await fetch(
          `${BASE_URL}/articles/?page=1&limit=10000`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        allArticles = data.data?.articles || [];
        
        // Cache the results for future searches
        allArticlesCacheRef.current = allArticles;
      }

      // Filter from cache - this is instant!
      const filtered = allArticles.filter((article) => {
        const titleMatch = article?.title?.toLowerCase().startsWith(term.toLowerCase());
        return titleMatch; // ONLY return titles that START WITH the search term
      });

      setFilteredArticles(filtered);
    } catch (error) {
      console.error("Error searching articles:", error);
      setFilteredArticles([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-between items-center mt-4 p-4">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> articles
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border"
          >
            Previous
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-1 border rounded-md ${
                currentPage === page
                  ? ""
                  : ""
              }`}
            >
              {page}
            </Button>
          ))}
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="workspace">
      {fileSizeError && (
        <div style={{position:"fixed",top:"20px",left:"50%",transform:"translateX(-50%)",zIndex:9999,display:"flex",alignItems:"flex-start",gap:"12px",backgroundColor:"#dc2626",color:"white",fontSize:"14px",fontWeight:"500",padding:"16px 20px",borderRadius:"10px",boxShadow:"0 10px 40px rgba(0,0,0,0.35)",maxWidth:"500px",width:"calc(100% - 32px)"}}>
          <span style={{fontSize:"20px",lineHeight:"1",flexShrink:0}}>🚫</span>
          <div style={{flex:1,lineHeight:"1.5"}}>{fileSizeError}</div>
          <button onClick={()=>setFileSizeError("")} style={{marginLeft:"8px",background:"none",border:"none",color:"white",fontSize:"22px",lineHeight:"1",cursor:"pointer",flexShrink:0,fontWeight:"bold",padding:0}}>×</button>
        </div>
      )}
      <Breadcrumb title="Articles">
        <BreadcrumbItem link="#no-link">Pages</BreadcrumbItem>
        <BreadcrumbItem>Articles</BreadcrumbItem>
      </Breadcrumb>

      {error && (
        <div className="alert-danger mb-4">
          {error}
          <button
            className="float-right text-white"
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert-success mb-4">
          {successMessage}
          <button
            className="float-right text-white"
            onClick={() => setSuccessMessage("")}
          >
            ×
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Article Management</h1>
        <div className="flex gap-2">
          <button onClick={openCreateModal} className="btn-primary">
            <span className="la la-plus"></span> Add New Article
          </button>
          <button
            onClick={() => {
              setSearchTerm("");
              fetchArticles();
            }}
            className="btn-secondary"
            disabled={loading}
          >
            <span className="la la-refresh"></span> Refresh
          </button>

        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-64">
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          {loading && !articles.length ? (
            <div className="text-center py-8">
              <span className="la la-spinner la-spin text-2xl"></span>
              <p className="mt-2">Loading articles...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover w-full">
                <thead>
    <tr>
      <th className="w-1/5">Title</th>
      <th className="w-1/5">Practice Area</th>
      <th className="w-1/5">Authors</th>
      <th className="w-1/5">Date</th>
      <th className="w-1/5">Actions</th>
    </tr>
  </thead>
                  <tbody>
    {filteredArticles.length === 0 ? (
      <tr>
        <td colSpan="5" className="text-center py-8">
          {articles.length === 0
            ? "No articles found. Create your first article!"
            : "No articles match your search"}
        </td>
      </tr>
    ) : (
      filteredArticles.map((article) => (
        <tr key={article.id}>
          <td className="w-1/4">
            <div className="font-medium truncate max-w-xs" title={article.title}>
              {article.title}
            </div>
          </td>
          <td className="w-1/4">
            <div className="truncate max-w-xs" title={article.practice_area?.name}>
              {article.practice_area?.name || "N/A"}
            </div>
          </td>
          <td className="w-1/4">
            {article.author && article.author.length > 0 
              ? article.author.map(a => a.full_name).join(", ") 
              : "No authors"}
          </td>

          <td className="w-1/5">
      {new Date(article.date).toLocaleDateString()}
    </td>


          <td className="w-1/4">
            <div className="flex gap-1">
              <Button
                onClick={() => openDetailsModal(article.id)}
                className="btn btn-sm btn-info"
                title="View Details"
              >
                <span className="la la-eye"></span>
              </Button>
              <Button
                onClick={() => openEditModal(article)}
                className="btn btn-sm btn-warning"
                title="Edit Article"
              >
                <span className="la la-edit"></span>
              </Button>
              <Button
                onClick={() => openDeleteModal(article.id)}
                color="danger"
                className="btn btn-sm btn-danger"
                title="Delete Article"
              >
                <span className="la la-trash"></span>
              </Button>
            </div>
          </td>
        </tr>
      ))
    )}
  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Create New Article
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleCreateArticle}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Content *
                      </label>
<RichTextEditor
  value={formData.content}
  onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
  uploadEndpoint={`${BASE_URL}/articles/editor/upload`}
/>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Practice Area *
                      </label>
                      <select
                        name="practice_area"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.practice_area}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a practice area</option>
                        {practiceAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Authors
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="authors"
                          value={tempArrayInput.authors || ""}
                          onChange={handleArrayInputChange}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select an author</option>
                          {authors.map((author) => (
                            <option key={author.id} value={author.id}>
                              {author.full_name}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          onClick={() => addArrayItem("authors")}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add
                        </Button>
                      </div>
                      <div className="mt-2">
                        {renderArrayItems(formData.authors)}
                      </div>
                    </div>

                    <div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Date *
  </label>
  <Input
    type="date"
    name="date"
    value={formData.date}
    onChange={handleInputChange}
    required
  />
</div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Article Image
                      </label>
                      <input
                        type="file"
                        name="image"
                        onChange={(e) => { const f=e.target.files[0]; if(!f) return; if(f.size > 2097152){ setFileSizeError(`⚠️ "${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`); e.target.value=""; return; } setFileSizeError(""); setFile(f); }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        accept="image/*"
                      />
                      {file && (
                        <p className="text-sm text-gray-500 mt-1">
                          {file.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                    <Button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <span className="la la-spinner la-spin mr-2"></span>
                          Creating...
                        </span>
                      ) : "Submit"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-lg relative w-auto my-6 mx-auto max-w-3xl">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Edit Article
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <form onSubmit={handleUpdateArticle}>
                  <div className="modal-body p-6 space-y-6">
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Title *
                      </label>
                      <Input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                  <div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Content *
  </label>
  <RichTextEditor
    value={formData.content}
    onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
    uploadEndpoint={`${BASE_URL}/articles/editor/upload`}
  />
</div>
                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Practice Area *
                      </label>
                      <select
                        name="practice_area"
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.practice_area}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a practice area</option>
                        {practiceAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Authors
                      </label>
                      <div className="flex gap-2">
                        <select
                          name="authors"
                          value={tempArrayInput.authors || ""}
                          onChange={handleArrayInputChange}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select an author</option>
                          {authors.map((author) => (
                            <option key={author.id} value={author.id}>
                              {author.full_name}
                            </option>
                          ))}
                        </select>
                        <Button
                          type="button"
                          onClick={() => addArrayItem("authors")}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add
                        </Button>
                      </div>
                      <div className="mt-2">
                        {renderArrayItems(formData.authors)}
                      </div>
                    </div>

                    <div className="form-group space-y-2">
  <label className="block text-sm font-medium text-gray-700">
    Date *
  </label>
  <Input
    type="date"
    name="date"
    value={formData.date}
    onChange={handleInputChange}
    required
  />
</div>

                    <div className="form-group space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Article Image
                      </label>
                      <input
                        type="file"
                        name="image"
                        onChange={(e) => { const f=e.target.files[0]; if(!f) return; if(f.size > 2097152){ setFileSizeError(`⚠️ "${f.name}" is ${(f.size/1048576).toFixed(1)} MB — over the 2 MB limit. Please use a smaller file.`); e.target.value=""; return; } setFileSizeError(""); setFile(f); }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        accept="image/*"
                      />
                      {file && (
                        <p className="text-sm text-gray-500 mt-1">
                          {file.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b space-x-4">
                    <Button
                      type="button"
                      onClick={closeAllModals}
                      className="btn-secondary"
                    >
                      Close
                    </Button>
                    <Button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <span className="la la-spinner la-spin mr-2"></span>
                          Updating...
                        </span>
                      ) : "Update Article"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show fixed inset-0 overflow-y-auto"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeAllModals}
        >
          <div
            className="flex items-center justify-center min-h-screen"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog relative w-auto my-6 mx-auto max-w-lg">
              <div className="modal-content relative bg-white rounded-lg shadow-lg">
                <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
                  <h5 className="modal-title text-xl font-semibold text-gray-800">
                    Confirm Delete
                  </h5>
                  <button
                    onClick={closeAllModals}
                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                  >
                    <span className="la la-times text-2xl"></span>
                  </button>
                </div>

                <div className="modal-body p-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete this article? This action
                    cannot be undone.
                  </p>
                </div>

                <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
                  <Button
                    onClick={closeAllModals}
                    className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteArticle}
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="la la-spinner la-spin mr-2"></span>
                        Deleting...
                      </span>
                    ) : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

{/* Details Modal */}
{showDetailsModal && (
  <div
    className="modal fade show fixed inset-0 overflow-y-auto"
    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    onClick={closeAllModals}
  >
    <div
      className="flex items-center justify-center min-h-screen"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="modal-dialog relative w-auto my-6 mx-auto max-w-4xl">
        <div className="modal-content relative bg-white rounded-lg shadow-lg">
          <div className="modal-header flex items-start justify-between p-6 border-b rounded-t">
            <h5 className="modal-title text-xl font-semibold text-gray-800">
              Article Details
            </h5>
            <button
              onClick={closeAllModals}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
            >
              <span className="la la-times text-2xl"></span>
            </button>
          </div>

          <div className="modal-body p-6">
            {loading ? (
              <div className="text-center py-8">
                <span className="la la-spinner la-spin text-2xl"></span>
                <p className="mt-2">Loading article details...</p>
              </div>
            ) : currentArticle ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="text-sm font-medium text-gray-500">Title</h3>
                  </div>
                  <div className="col-span-3">
                    <p className="text-gray-900 font-medium">{currentArticle.title || "N/A"}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="text-sm font-medium text-gray-500">Content</h3>
                  </div>
                  <div className="col-span-3">
                    <div 
                      className="prose max-w-none overflow-y-auto max-h-[50vh]
                                prose-headings:text-gray-800
                                prose-p:text-gray-700 prose-p:my-2
                                prose-ul:list-disc prose-ul:pl-5 prose-ul:my-2
                                prose-ol:list-decimal prose-ol:pl-5 prose-ol:my-2
                                prose-li:my-1 prose-li:pl-1
                                prose-strong:font-semibold
                                prose-em:italic
                                prose-a:text-blue-600 prose-a:underline hover:prose-a:text-blue-800
                                prose-img:rounded-lg prose-img:shadow-md prose-img:my-3"
                      dangerouslySetInnerHTML={{ 
                        __html: currentArticle.content?.html || currentArticle.content || "No content available" 
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="text-sm font-medium text-gray-500">Date</h3>
                  </div>
                  <div className="col-span-3">
                    <p className="text-gray-900">
                      {new Date(currentArticle.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="text-sm font-medium text-gray-500">Practice Area</h3>
                  </div>
                  <div className="col-span-3">
                    <p className="text-gray-900">
                      {currentArticle.practice_area?.name || "No practice area assigned"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="col-span-1">
                    <h3 className="text-sm font-medium text-gray-500">Authors</h3>
                  </div>
                  <div className="col-span-3">
                    {currentArticle?.author?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {currentArticle.author.map((author, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                          >
                            {author.full_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">No authors assigned</p>
                    )}
                  </div>
                </div>

                {currentArticle.file_url && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="col-span-1">
                      <h3 className="text-sm font-medium text-gray-500">Attachment</h3>
                    </div>
                    <div className="col-span-3">
                      <a
                        href={currentArticle.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <span className="la la-file-alt mr-2 text-lg"></span>
                        {currentArticle.file_url.split('/').pop()}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-700">Failed to load article details</p>
              </div>
            )}
          </div>

          <div className="modal-footer flex justify-end p-6 border-t border-gray-200 rounded-b">
            <Button
              onClick={closeAllModals}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

      <Footer />
    </main>
  );
};

export default Article;